import { Box, Flex, HStack, Input, useToast, VStack } from '@chakra-ui/react';
import { Button, showToast, Text } from '@diamond/shared/ui';
import React from 'react';
import { useFormContext } from 'react-hook-form';

type ParsedInfo = {
  name: string;
  size: number;
  previewLines: string[];
  totalRows: number; // numeric rows counted beneath header
};

export function ShipToCSV() {
  const toast = useToast();
  const { setValue, watch, clearErrors } = useFormContext();

  const shiptos: string | File | null = watch('shiptos');

  const [fileInfo, setFileInfo] = React.useState<ParsedInfo | null>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const downloadTemplate = () => {
    const csvContent = [
      'sap_no_shipto',
      '10604684',
      '11261284',
      '12500999',
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'shiptos_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleBrowseClick = () => inputRef.current?.click();

  const parseCsvText = (text: string, name: string, size: number) => {
    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    const header = (lines[0] || '').trim();
    const hasHeader = header.toLowerCase() === 'sap_no_shipto';
    const dataLines = lines
      .slice(1)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    const numericRows = dataLines.filter((l) => /^\d+$/.test(l));

    // non-blocking guidance only
    if (!hasHeader) {
      showToast(toast, 'warning', 'CSV tidak memiliki header sap_no_shipto');
    }

    setFileInfo({
      name,
      size,
      previewLines: dataLines.slice(0, 3),
      totalRows: numericRows.length,
    });
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.csv')) {
      showToast(toast, 'error', 'Hanya file .csv yang didukung');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      setValue('shiptos', text, { shouldDirty: true, shouldValidate: true });
      clearErrors('shiptos');
      parseCsvText(text, file.name, file.size);
    };
    reader.onerror = () => {
      showToast(toast, 'error', 'Gagal membaca file CSV');
    };
    reader.readAsText(file);
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer?.files) handleFiles(e.dataTransfer.files);
  };

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const removeFile = () => {
    setValue('shiptos', null, { shouldDirty: true, shouldValidate: true });
    setFileInfo(null);
  };

  return (
    <VStack align="start" spacing={4} mt={4}>
      <VStack align="start" spacing={3}>
        <HStack spacing={2} align="start">
          <Text as="span" fontWeight="bold" fontSize="lg">
            1.
          </Text>
          <Text fontSize="lg">
            Download template ship to .csv{' '}
            <Text
              as="span"
              color="blue.500"
              cursor="pointer"
              textDecoration="underline"
              onClick={downloadTemplate}
              _hover={{ color: 'blue.600' }}
            >
              disini
            </Text>
          </Text>
        </HStack>

        <HStack spacing={2} align="start">
          <Text as="span" fontWeight="bold" fontSize="lg">
            2.
          </Text>
          <VStack align="start" spacing={1}>
            <Text fontSize="lg">Upload template ship to .csv</Text>
            <Text fontSize="sm" color="gray.600">
              Maximum file size 2MB per upload.
            </Text>
          </VStack>
        </HStack>
      </VStack>

      <Box
        role="button"
        tabIndex={0}
        aria-label="Upload CSV file"
        onClick={handleBrowseClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleBrowseClick();
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        border="2px dashed"
        borderColor={
          isDragOver ? 'blue.400' : shiptos ? 'green.400' : 'gray.300'
        }
        borderRadius="8px"
        p={8}
        bg={isDragOver ? 'blue.50' : shiptos ? 'green.50' : 'gray.50'}
        cursor="pointer"
        transition="all 0.2s"
        w="100%"
        textAlign="center"
      >
        <VStack spacing={2}>
          <Text fontSize="lg" color="gray.600">
            Drag n Drop here or{' '}
            <Text
              as="span"
              color="blue.500"
              cursor="pointer"
              textDecoration="underline"
              _hover={{ color: 'blue.600' }}
            >
              Browse File
            </Text>
          </Text>
        </VStack>
        <Input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => handleFiles(e.target.files)}
          display="none"
        />
      </Box>

      {shiptos && (
        <HStack spacing={2} pt={2}>
          <Button
            size="sm"
            variant="ghost"
            onClick={removeFile}
            aria-label="Hapus file CSV"
          >
            Remove File
          </Button>
        </HStack>
      )}

      {fileInfo && (
        <VStack align="start" spacing={1} pt={2}>
          <Text fontSize="sm">File: {fileInfo.name}</Text>
          <Text fontSize="sm">
            Ukuran: {(fileInfo.size / 1024).toFixed(1)} KB
          </Text>
          <Text fontSize="sm">Total parsed rows: {fileInfo.totalRows}</Text>
          {fileInfo.previewLines.length > 0 && (
            <Box>
              <Text fontSize="sm" color="gray.600">
                Preview:
              </Text>
              <Box
                maxH="200px"
                overflowY="auto"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="4px"
                p={2}
                bg="gray.50"
              >
                {fileInfo.previewLines.map((l, idx) => (
                  <Text key={idx} fontSize="sm" color="gray.700" py={1}>
                    {l}
                  </Text>
                ))}
              </Box>
            </Box>
          )}
        </VStack>
      )}
    </VStack>
  );
}
